import { useToast } from 'vue-toastification';

/**
 * Satu tempat untuk memberitahu pengguna bahwa sebuah permintaan gagal.
 *
 * MASALAH YANG DIPECAHKAN
 * Pemeriksaan menyeluruh menemukan 136 blok `catch` yang isinya hanya
 * `console.error(...)`. Konsol peramban tidak pernah dibuka oleh panitia
 * maupun pelatih, jadi yang mereka lihat cuma: tabel yang tidak pernah
 * terisi, tombol simpan yang seolah tidak bereaksi, angka yang tetap nol.
 * Tidak ada satu pun petunjuk bahwa yang bermasalah adalah jaringan atau
 * sesi yang berakhir.
 *
 * Memperbaiki 136 tempat satu per satu berarti 136 kesempatan untuk
 * terlewat, dan kode baru akan mengulang pola yang sama. Karena semuanya
 * lewat axios, pelaporannya dipasang di interceptor: mustahil ada
 * permintaan gagal yang lolos tanpa terlihat.
 *
 * PEREDAM PENGULANGAN
 * Satu halaman bisa memuat belasan permintaan sekaligus — halaman Tournament
 * Insight menembakkan 13. Kalau jaringan putus, tanpa peredam ini layar
 * dibanjiri 13 notifikasi berisi kalimat yang sama persis. Pesan yang
 * identik karena itu hanya ditampilkan sekali dalam selang waktu pendek.
 */

const JEDA_REDAM = 4000;
const terakhir = new Map();

let toast = null;

/** Diambil malas: useToast() hanya boleh dipanggil setelah aplikasi dibuat. */
function ambilToast() {
  if (!toast) {
    try {
      toast = useToast();
    } catch (e) {
      return null;
    }
  }
  return toast;
}

/**
 * Menampilkan pesan galat, meredam pengulangan pesan yang sama.
 * @returns {boolean} true bila pesannya benar-benar ditampilkan
 */
export function laporGalat(pesan) {
  const teks = String(pesan || '').trim();
  if (!teks) return false;

  const sekarang = Date.now();
  const sebelumnya = terakhir.get(teks);
  if (sebelumnya && sekarang - sebelumnya < JEDA_REDAM) return false;
  terakhir.set(teks, sekarang);

  // Peta dibersihkan agar tidak tumbuh selamanya pada sesi yang panjang.
  if (terakhir.size > 40) {
    for (const [k, waktu] of terakhir) {
      if (sekarang - waktu > JEDA_REDAM) terakhir.delete(k);
    }
  }

  const t = ambilToast();
  if (!t) return false;

  t.error(teks, { timeout: 5000 });
  return true;
}

/**
 * Memutuskan apakah sebuah kegagalan axios perlu diberitahukan.
 *
 * TIDAK dilaporkan:
 *   - 401. Pengguna sudah dilempar ke halaman masuk; notifikasi tambahan
 *     hanya menumpuk di atas halaman yang sudah berganti.
 *   - 422. Itu galat validasi formulir, dan tempatnya di bawah kolom yang
 *     bersangkutan — bukan di notifikasi melayang.
 *   - Permintaan yang dibatalkan (pindah halaman saat data masih dimuat).
 *   - Panggilan yang secara sadar minta didiamkan lewat `diamDiam: true`.
 */
export function perluDilaporkan(error) {
  if (!error) return false;
  if (error.config?.diamDiam) return false;
  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return false;

  const status = error.response?.status;
  if (status === 401 || status === 422) return false;

  return true;
}

export default { laporGalat, perluDilaporkan };
